import { useCallback } from 'react';
import { useAuthnMe } from '../AuthnMeProvider';

/**
 * Returns a function that builds request headers for API calls.
 * Uses JWT when AuthnMe is configured and user is authenticated, otherwise API key.
 * Proactively triggers a background token refresh if the token is expiring soon.
 * Use for pages that support both JWT and API key auth.
 */
export function useApiHeaders(apiKey: string = ''): () => Record<string, string> {
  const authnMe = useAuthnMe();
  const { getAuthHeaders, isAuthenticated, isConfigured } = authnMe;
  // Use the reactive `isConfigured` from context, NOT the module-level
  // isAuthnMeConfigured() — the latter reads a null-until-fetched global, so a
  // component that mounts before /api/auth/config resolves (e.g. SandboxRail on
  // the requireLogin-wrapped Configure page) sees it as false and drops the
  // Authorization header, 401-ing owner-only calls like sandbox creation. The
  // context state updates after the fetch and re-renders consumers.
  const isJwtAuthenticated = isConfigured && isAuthenticated;

  return useCallback((): Record<string, string> => {
    if (isJwtAuthenticated) {
      // Use the provider's getAuthHeaders which handles background refresh
      // for tokens expiring within 2 minutes
      const authHeaders = getAuthHeaders();
      // Bearer for AuthnMe sessions; X-API-Key for `/?apikey=` link sessions.
      if (authHeaders['Authorization'] || authHeaders['X-API-Key']) {
        return {
          'Content-Type': 'application/json',
          ...authHeaders,
        };
      }
    }
    return {
      'Content-Type': 'application/json',
      ...(apiKey ? { 'X-API-Key': apiKey } : {}),
    };
  }, [isJwtAuthenticated, getAuthHeaders, apiKey]);
}
